/**
 * =============================================================================
 * Página: ReferralCommissions (Comissões do módulo Indicação)
 * Versão: 2.1.0
 *
 * Descrição:
 *   Gestão administrativa de comissões do tenant. Padronizada com a mesma
 *   estrutura visual e arquitetural da página de Cobranças do módulo Financeiro
 *   (`FinancialCharges.tsx`) e da página de Pagamentos
 *   (`ReferralPayouts.tsx` v2.0.0):
 *
 *   - Header compacto com título + descrição + MonthNavigator + Refresh.
 *   - 4 KPI cards horizontais coloridos (Pendentes, Aprovadas, Pagas, Canceladas).
 *   - Barra inline de filtros rápidos + botão "Mais" abrindo Sheet lateral
 *     com filtros avançados (campanha, tipo, faixas de data, only-unpaid).
 *   - Tabela padrão + PaginationFooter.
 *   - 100% server-side via RPC `get_referral_commissions_paginated`
 *     (KPIs + total_count + linhas em uma única chamada). NENHUM SELECT
 *     direto na tabela é feito a partir desta página.
 *   - Realtime centralizado: invalida a queryKey paginada em qualquer
 *     mudança em `affiliate_commissions`.
 *
 *   IMPORTANTE: Esta página NÃO interage com o módulo Financeiro. Toda a
 *   moderação (aprovar/cancelar/marcar paga) usa as RPCs administrativas
 *   já existentes em `useReferralCommissionsAdmin` (preservadas).
 *
 * Release notes:
 *   v1.0.0 - Versão inicial (client-side, KPIs/busca client-side, sem paginação).
 *   v2.0.0 - Padronização com Financeiro/Cobranças e Payouts. Migração para
 *            RPC paginada `get_referral_commissions_paginated`. KPIs server-side,
 *            MonthNavigator, Sheet de filtros avançados, PaginationFooter,
 *            Realtime centralizado.
 *   v2.1.0 - Refatorada para usar os helpers de layout reutilizáveis
 *            (`ListPageLayout`, `ListPageHeader`, `ListPageKpis`, `KpiCard`,
 *            `ListPageFiltersBar`, `ListPageContent`, `ListPageFooter`),
 *            garantindo paridade visual 99,99% com FinancialCharges.
 * =============================================================================
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  HandCoins,
  Search,
  CheckCircle2,
  XCircle,
  CircleDollarSign,
  Clock,
  MoreHorizontal,
  RefreshCw,
  SlidersHorizontal,
  CalendarIcon,
  CircleDot,
  Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  startOfMonth,
  endOfMonth,
  format as formatDateFns,
} from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { CommissionsTableView } from '@/components/referral/CommissionsTableView';
import { MonthNavigator } from '@/components/financial/MonthNavigator';
import {
  calculatePeriodDates,
  type PeriodPreset,
} from '@/hooks/useCrmOverviewFilters';
// Helpers de layout padronizado (paridade visual com FinancialCharges)
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
  ListPageFooter,
} from '@/components/layout/list-page';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { createLogger } from '@/lib/logger';
import {
  useReferralCommissionsPaginated,
  type CommissionStatus,
  type RewardType,
  type CommissionPaginatedFilters,
} from '@/hooks/useReferralCommissionsPaginated';
import { useReferralCommissionMutations } from '@/hooks/useReferralCommissionsAdmin';
import { useCreatePayoutForCommission } from '@/hooks/useCreatePayoutForCommission';
import { useNavigate } from 'react-router-dom';
import { ReferralAffiliateSelector } from '@/components/referral/ReferralAffiliateSelector';
import { useReferralCampaigns } from '@/hooks/useReferralCampaigns';
import { useReferralPrograms } from '@/hooks/useReferralPrograms';
// Componentes premium reutilizados (mesmos modais usados em Pagamentos)
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
// Drill-down: pagamentos vinculados a UMA comissão
import { CommissionPayoutsModal } from '@/components/referral/CommissionPayoutsModal';
// Sheet lateral de detalhes da comissão (v2.3.0)
import { CommissionDetailSheet } from '@/components/referral/CommissionDetailSheet';
// Picker server-side (busca remota — bypassa o limite de 1k do PostgREST)
import { TenantEntityPicker } from '@/components/referral/TenantEntityPicker';
import { ReferralUsageInline } from '@/components/referral/billing/ReferralUsageInline';

const logger = createLogger('ReferralCommissions');

// ============================================================================
// CONSTANTES DE EXIBIÇÃO
// ============================================================================

/** Labels de status (PT-BR). */
const STATUS_LABEL: Record<CommissionStatus, string> = {
  pending: 'Pendente',
  approved: 'Aprovada',
  paid: 'Paga',
  cancelled: 'Cancelada',
  reversed: 'Revertida',
};

/**
 * Cores de badge de status — mesmo padrão semântico do FinancialCharges/Payouts
 * (`bg-{color}-100 text-{color}-800 dark:bg-{color}-900/30 dark:text-{color}-400`).
 */
const STATUS_BADGE_COLORS: Record<CommissionStatus, string> = {
  pending:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  approved:
    'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  paid:
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  cancelled:
    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  reversed:
    'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
};

/** Labels de tipo de recompensa (PT-BR). */
const REWARD_TYPE_LABEL: Record<RewardType, string> = {
  percentage: 'Percentual',
  fixed: 'Valor fixo',
  points: 'Pontos',
  recurring: 'Recorrente',
  tiered: 'Em níveis',
};

/** Formata valor monetário em BRL. */
function formatBRL(value: number | null | undefined) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value ?? 0));
}

/** Formata data ISO em pt-BR (dd/mm/yyyy). */
function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function ReferralCommissions() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // =========================================================================
  // Estados de filtro
  // =========================================================================
  const [statusFilter, setStatusFilter] = useState<CommissionStatus | 'all'>('all');
  const [affiliateFilter, setAffiliateFilter] = useState<string | 'all'>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Filtro por mês (header — afeta created_at)
  const [monthFilter, setMonthFilter] = useState<Date | null>(new Date());
  // v2.1.0 (2026-07-24) — Preset do MonthNavigator.
  const [activePreset, setActivePreset] = useState<PeriodPreset | null>('month');
  const [customRange, setCustomRange] = useState<{ startDate: Date; endDate: Date } | null>(null);
  const [dataCriacaoDe, setDataCriacaoDe] = useState<string | null>(
    () => formatDateFns(startOfMonth(new Date()), 'yyyy-MM-dd'),
  );
  const [dataCriacaoAte, setDataCriacaoAte] = useState<string | null>(
    () => formatDateFns(endOfMonth(new Date()), 'yyyy-MM-dd'),
  );

  // Filtros avançados (Sheet "Mais")
  const [campaignFilter, setCampaignFilter] = useState<string | 'all'>('all');
  const [rewardTypeFilter, setRewardTypeFilter] = useState<RewardType | 'all'>('all');
  const [dataAprovacaoDe, setDataAprovacaoDe] = useState<string | null>(null);
  const [dataAprovacaoAte, setDataAprovacaoAte] = useState<string | null>(null);
  const [dataPagamentoDe, setDataPagamentoDe] = useState<string | null>(null);
  const [dataPagamentoAte, setDataPagamentoAte] = useState<string | null>(null);
  const [onlyUnpaid, setOnlyUnpaid] = useState(false);
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);

  // -------------------------------------------------------------------------
  // Novos filtros de rastreabilidade (v2.2.0)
  // - customerFilter / sellerFilter: usam TenantEntityPicker (server-side,
  //   bypassa o limite de 1k registros do PostgREST).
  // - programFilter: combo simples (programs do tenant).
  // -------------------------------------------------------------------------
  const [customerFilter, setCustomerFilter] = useState<string | null>(null);
  const [sellerFilter, setSellerFilter] = useState<string | null>(null);
  const [programFilter, setProgramFilter] = useState<string | 'all'>('all');

  // -------------------------------------------------------------------------
  // Estados dos modais de drill-down (v2.2.0)
  //   • Negócio (DealDetailsModalPremium) — coluna "N. Negócio"
  //   • Cliente (CustomerDetailsModalPremium) — coluna "Cliente"
  //   • Pagamentos (CommissionPayoutsModal) — coluna "Pagamentos"
  // -------------------------------------------------------------------------
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [payoutsCommissionId, setPayoutsCommissionId] = useState<string | null>(null);
  // v2.3.0 — Sheet lateral de detalhes da comissão.
  const [detailsCommissionId, setDetailsCommissionId] = useState<string | null>(null);

  // Paginação server-side
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Diálogo de cancelamento (preservado da v1)
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  // =========================================================================
  // Hooks de dados
  // =========================================================================
  // Filtro de afiliado agora usa busca server-side via ReferralAffiliateSelector
  // (Popover + Command com debounce), substituindo o antigo Select estático.
  const { data: campaigns = [] } = useReferralCampaigns();
  const { data: programs = [] } = useReferralPrograms();
  // markPaidMutation foi descontinuado: agora geramos um payout e
  // redirecionamos o admin para a tela Pagamentos (única fonte de verdade
  // do extrato de pagamentos de comissões).
  const { approveMutation, cancelMutation } = useReferralCommissionMutations();
  const createPayoutMutation = useCreatePayoutForCommission();
  const navigate = useNavigate();

  // Debounce da busca textual (300ms) — mesmo padrão de FinancialCharges
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Monta filtros para a RPC
  const filters: CommissionPaginatedFilters = useMemo(
    () => ({
      status: statusFilter !== 'all' ? statusFilter : null,
      affiliate_id: affiliateFilter !== 'all' ? affiliateFilter : null,
      campaign_id: campaignFilter !== 'all' ? campaignFilter : null,
      reward_type: rewardTypeFilter !== 'all' ? rewardTypeFilter : null,
      search: debouncedSearch || null,
      data_criacao_de: dataCriacaoDe,
      data_criacao_ate: dataCriacaoAte,
      data_aprovacao_de: dataAprovacaoDe,
      data_aprovacao_ate: dataAprovacaoAte,
      data_pagamento_de: dataPagamentoDe,
      data_pagamento_ate: dataPagamentoAte,
      only_unpaid: onlyUnpaid,
      // Rastreabilidade (v2.2.0)
      customer_id: customerFilter,
      seller_id: sellerFilter,
      program_id: programFilter !== 'all' ? programFilter : null,
    }),
    [
      statusFilter,
      affiliateFilter,
      campaignFilter,
      rewardTypeFilter,
      debouncedSearch,
      dataCriacaoDe,
      dataCriacaoAte,
      dataAprovacaoDe,
      dataAprovacaoAte,
      dataPagamentoDe,
      dataPagamentoAte,
      onlyUnpaid,
      customerFilter,
      sellerFilter,
      programFilter,
    ],
  );

  const {
    commissions,
    totalCount,
    kpis,
    isLoading,
    isFetching,
    refetch,
  } = useReferralCommissionsPaginated(filters, {
    page: currentPage,
    pageSize,
  });

  const { isRefreshing, triggerRefresh } = useRefreshWithTimestamp();

  // =========================================================================
  // Realtime centralizado — invalida cache em qualquer mudança em comissões
  // =========================================================================
  useEffect(() => {
    if (!currentTenant?.id) return;

    const channel = supabase
      .channel(`affiliate-commissions-page-rt-${currentTenant.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affiliate_commissions',
          filter: `tenant_id=eq.${currentTenant.id}`,
        },
        (payload) => {
          logger.info('Realtime: comissão alterada', {
            eventType: payload.eventType,
          });
          queryClient.invalidateQueries({
            queryKey: ['referral-commissions-paginated', currentTenant.id],
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentTenant?.id, queryClient]);

  // Reseta página ao mudar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearch,
    statusFilter,
    affiliateFilter,
    campaignFilter,
    rewardTypeFilter,
    dataCriacaoDe,
    dataCriacaoAte,
    dataAprovacaoDe,
    dataAprovacaoAte,
    dataPagamentoDe,
    dataPagamentoAte,
    onlyUnpaid,
    customerFilter,
    sellerFilter,
    programFilter,
  ]);

  // =========================================================================
  // Handlers de UI
  // =========================================================================
  const handleRefresh = useCallback(() => {
    triggerRefresh(async () => {
      refetch();
    });
  }, [triggerRefresh, refetch]);

  const handleMonthChange = useCallback((newDate: Date) => {
    setMonthFilter(newDate);
    setActivePreset('month');
    setCustomRange(null);
    setDataCriacaoDe(formatDateFns(startOfMonth(newDate), 'yyyy-MM-dd'));
    setDataCriacaoAte(formatDateFns(endOfMonth(newDate), 'yyyy-MM-dd'));
  }, []);

  const handleMonthClear = useCallback(() => {
    setMonthFilter(null);
    setActivePreset(null);
    setCustomRange(null);
    setDataCriacaoDe(null);
    setDataCriacaoAte(null);
  }, []);

  /** v2.1.0 — Aplica preset ao filtro de data de criação. */
  const handlePresetChange = useCallback((preset: PeriodPreset) => {
    setActivePreset(preset);
    if (preset === 'month') {
      const now = new Date();
      setMonthFilter(now);
      setCustomRange(null);
      setDataCriacaoDe(formatDateFns(startOfMonth(now), 'yyyy-MM-dd'));
      setDataCriacaoAte(formatDateFns(endOfMonth(now), 'yyyy-MM-dd'));
      return;
    }
    setCustomRange(null);
    const { startDate, endDate } = calculatePeriodDates(preset);
    setDataCriacaoDe(formatDateFns(startDate, 'yyyy-MM-dd'));
    setDataCriacaoAte(formatDateFns(endDate, 'yyyy-MM-dd'));
  }, []);

  const handleCustomPeriodChange = useCallback((startDate: Date, endDate: Date) => {
    setActivePreset('custom');
    setCustomRange({ startDate, endDate });
    setDataCriacaoDe(formatDateFns(startDate, 'yyyy-MM-dd'));
    setDataCriacaoAte(formatDateFns(endDate, 'yyyy-MM-dd'));
  }, []);

  /**
   * Contador de filtros avançados ativos (badge do botão "Mais").
   * NOTA: `rewardTypeFilter` foi promovido para a barra inline (paridade
   * com CrmDeals) e por isso não conta mais aqui.
   */
  const advancedFilterCount = useMemo(() => {
    let count = 0;
    if (campaignFilter !== 'all') count++;
    if (programFilter !== 'all') count++;
    if (customerFilter) count++;
    if (sellerFilter) count++;
    if (dataAprovacaoDe || dataAprovacaoAte) count++;
    if (dataPagamentoDe || dataPagamentoAte) count++;
    if (onlyUnpaid) count++;
    return count;
  }, [
    campaignFilter,
    programFilter,
    customerFilter,
    sellerFilter,
    dataAprovacaoDe,
    dataAprovacaoAte,
    dataPagamentoDe,
    dataPagamentoAte,
    onlyUnpaid,
  ]);

  const clearAdvancedFilters = useCallback(() => {
    setCampaignFilter('all');
    setProgramFilter('all');
    setCustomerFilter(null);
    setSellerFilter(null);
    setRewardTypeFilter('all');
    setDataAprovacaoDe(null);
    setDataAprovacaoAte(null);
    setDataPagamentoDe(null);
    setDataPagamentoAte(null);
    setOnlyUnpaid(false);
  }, []);

  // =========================================================================
  // Mutations — handlers
  // =========================================================================
  const handleApprove = async (id: string) => {
    try {
      await approveMutation.mutateAsync(id);
      toast.success('Comissão aprovada com sucesso.');
    } catch (e: any) {
      toast.error(e?.message || 'Falha ao aprovar comissão.');
    }
  };

  /**
   * Atalho "Pagar agora" — cria (ou reaproveita) um payout para a comissão
   * e redireciona o admin para a tela Pagamentos, onde o pagamento é
   * efetivamente confirmado via `confirm_payout`. Isso garante que TODA
   * comissão paga apareça no extrato (`affiliate_payouts`).
   */
  const handleMarkPaid = async (id: string) => {
    try {
      const result = await createPayoutMutation.mutateAsync(id);
      toast.success(
        result.reused
          ? 'Pagamento já existente reaberto. Conclua na tela Pagamentos.'
          : 'Pagamento criado. Conclua na tela Pagamentos.',
      );
      navigate(
        `/tenant/indicacao/pagamentos?highlight=${result.payout_id}&action=pay`,
      );
    } catch (e: any) {
      toast.error(e?.message || 'Falha ao gerar pagamento da comissão.');
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    try {
      await cancelMutation.mutateAsync({
        id: cancelTarget,
        reason: cancelReason.trim() || null,
      });
      toast.success('Comissão cancelada.');
      setCancelTarget(null);
      setCancelReason('');
    } catch (e: any) {
      toast.error(e?.message || 'Falha ao cancelar comissão.');
    }
  };

  // =========================================================================
  // Render
  // =========================================================================
  return (
    <ListPageLayout>
      {/* ================================================================ */}
      {/* Header padronizado (helper)                                       */}
      {/* ================================================================ */}
      <ListPageHeader
        title="Comissões"
        description="Aprove, cancele ou marque comissões como pagas."
        center={
          monthFilter || (activePreset && activePreset !== 'month') ? (
            <MonthNavigator
              currentMonth={monthFilter ?? new Date()}
              onChange={handleMonthChange}
              onClear={handleMonthClear}
              activePreset={activePreset}
              customRange={customRange}
              onPresetChange={handlePresetChange}
              onCustomChange={handleCustomPeriodChange}
            />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleMonthChange(new Date())}
              className="text-xs text-muted-foreground"
            >
              <CalendarIcon className="h-3.5 w-3.5 mr-1" />
              Filtrar por mês
            </Button>
          )
        }
        actions={
          // Padrão TaskList: indicadores acima, botões de ação abaixo
          <div className="flex flex-col items-end gap-1.5">
            <ReferralUsageInline className="hidden md:inline-flex" />
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="icon-sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                title="Atualizar dados"
              >
                <RefreshCw
                  className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')}
                />
              </Button>
            </div>
          </div>
        }
      />

      {/* ================================================================ */}
      {/* KPIs padronizados (helper)                                        */}
      {/* ================================================================ */}
      {!isLoading && (
        <ListPageKpis>
          <KpiCard color="yellow" icon={Clock} label="Pendentes" value={formatBRL(kpis.totalPendente)} />
          <KpiCard color="blue" icon={CheckCircle2} label="Aprovadas" value={formatBRL(kpis.totalAprovada)} />
          <KpiCard color="green" icon={CircleDollarSign} label="Pagas" value={formatBRL(kpis.totalPaga)} />
          <KpiCard color="red" icon={XCircle} label="Canceladas" value={formatBRL(kpis.totalCancelada)} />
        </ListPageKpis>
      )}

      {/* ================================================================ */}
      {/* Barra de filtros rápidos (helper, paridade com Cobranças)         */}
      {/* ================================================================ */}
      <ListPageFiltersBar
        onMoreClick={() => setIsMoreFiltersOpen(true)}
        moreCount={advancedFilterCount}
        isFetching={isFetching && !isLoading}
      >
        {/* Busca textual com debounce */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por afiliado, cliente, vendedor, programa, negócio ou cole um ID (8+ chars)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cn('pl-8', LIST_FILTERS_INPUT_CLASS)}
          />
        </div>

        {/* Filtro por afiliado — busca server-side (nome, e-mail, telefone, CPF/CNPJ) */}
        <div className="w-full sm:w-[220px]">
          <ReferralAffiliateSelector
            value={affiliateFilter === 'all' ? null : affiliateFilter}
            onChange={(id) => setAffiliateFilter(id ?? 'all')}
            placeholder="Filtrar por afiliado..."
          />
        </div>

        {/* Filtro por Status (inline — paridade com CrmDeals) */}
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as CommissionStatus | 'all')}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[140px]')}>
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <CircleDot className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate min-w-0 flex-1 text-left">
                {statusFilter === 'all'
                  ? 'Status'
                  : STATUS_LABEL[statusFilter as CommissionStatus]}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="pending">
              <span className="flex items-center gap-2">
                <CircleDot className="h-3.5 w-3.5 text-yellow-500" />
                Pendente
              </span>
            </SelectItem>
            <SelectItem value="approved">
              <span className="flex items-center gap-2">
                <CircleDot className="h-3.5 w-3.5 text-blue-500" />
                Aprovada
              </span>
            </SelectItem>
            <SelectItem value="paid">
              <span className="flex items-center gap-2">
                <CircleDot className="h-3.5 w-3.5 text-green-500" />
                Paga
              </span>
            </SelectItem>
            <SelectItem value="cancelled">
              <span className="flex items-center gap-2">
                <CircleDot className="h-3.5 w-3.5 text-red-500" />
                Cancelada
              </span>
            </SelectItem>
            <SelectItem value="reversed">
              <span className="flex items-center gap-2">
                <CircleDot className="h-3.5 w-3.5 text-muted-foreground" />
                Revertida
              </span>
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Filtro por Tipo de recompensa (inline — paridade com CrmDeals) */}
        <Select
          value={rewardTypeFilter}
          onValueChange={(v) => setRewardTypeFilter(v as RewardType | 'all')}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[140px]')}>
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <Tag className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate min-w-0 flex-1 text-left">
                {rewardTypeFilter === 'all'
                  ? 'Tipo'
                  : REWARD_TYPE_LABEL[rewardTypeFilter as RewardType]}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {(Object.keys(REWARD_TYPE_LABEL) as RewardType[]).map((k) => (
              <SelectItem key={k} value={k}>
                {REWARD_TYPE_LABEL[k]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      {/* ================================================================ */}
      {/* Tabela (helper) — full-bleed para paridade com CrmDeals           */}
      {/* ================================================================ */}
      <ListPageContent fullBleed>
        <CommissionsTableView
          commissions={commissions}
          isLoading={isLoading}
          onAction={(c, action) => {
            if (action === 'view') setDetailsCommissionId(c.id);
            else if (action === 'approve') handleApprove(c.id);
            else if (action === 'mark_paid') handleMarkPaid(c.id);
            else if (action === 'cancel') setCancelTarget(c.id);
          }}
          emptyMessage="Nenhuma comissão encontrada com os filtros aplicados"
          // v2.3.0 — abre o Sheet lateral de detalhes ao clicar em "Ref."
          // ou no item "Ver detalhes" do menu da linha.
          onOpenDetails={(c) => setDetailsCommissionId(c.id)}
          // Drill-down: pagamentos vinculados à comissão
          onOpenPayouts={(c) => setPayoutsCommissionId(c.id)}
          // Drill-down: modal premium do negócio (CRM)
          onOpenDeal={(dealId) => {
            setSelectedDealId(dealId);
            setIsDealModalOpen(true);
          }}
          // Drill-down: modal premium do cliente (CRM)
          onOpenCustomer={(customerId) => {
            setSelectedCustomerId(customerId);
            setIsCustomerModalOpen(true);
          }}
        />
      </ListPageContent>

      {/* ================================================================ */}
      {/* Footer de paginação server-side (helper)                          */}
      {/* ================================================================ */}
      {!isLoading && totalCount > 0 && (
        <ListPageFooter>
          <PaginationFooter
            totalItems={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        </ListPageFooter>
      )}

      {/* ============================================================= */}
      {/* Sheet lateral — Filtros avançados                              */}
      {/* ============================================================= */}
      <Sheet open={isMoreFiltersOpen} onOpenChange={setIsMoreFiltersOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetHeader className="p-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              Mais filtros
            </SheetTitle>
          </SheetHeader>

          <ScrollArea className="flex-1">
            <div className="p-4 space-y-5">
              {/*
                NOTA: O filtro "Tipo de recompensa" foi promovido para a barra
                inline de filtros rápidos (paridade com CrmDeals).
              */}

              {/* Campanha */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Campanha
                </Label>
                <Select
                  value={campaignFilter}
                  onValueChange={(v) => setCampaignFilter(v)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todas as campanhas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as campanhas</SelectItem>
                    {campaigns.map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Programa de indicação (v2.2.0) */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Programa de indicação
                </Label>
                <Select
                  value={programFilter}
                  onValueChange={(v) => setProgramFilter(v)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos os programas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os programas</SelectItem>
                    {programs.map((p: any) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Cliente (busca server-side via TenantEntityPicker) */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Cliente
                </Label>
                <TenantEntityPicker
                  entity="customer"
                  value={customerFilter}
                  onChange={setCustomerFilter}
                  placeholder="Buscar cliente..."
                />
              </div>

              {/* Vendedor (busca server-side via TenantEntityPicker) */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Vendedor
                </Label>
                <TenantEntityPicker
                  entity="seller"
                  value={sellerFilter}
                  onChange={setSellerFilter}
                  placeholder="Buscar vendedor..."
                />
              </div>

              <Separator />
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Aprovação
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={dataAprovacaoDe ?? ''}
                    onChange={(e) =>
                      setDataAprovacaoDe(e.target.value || null)
                    }
                    className="h-9"
                  />
                  <Input
                    type="date"
                    value={dataAprovacaoAte ?? ''}
                    onChange={(e) =>
                      setDataAprovacaoAte(e.target.value || null)
                    }
                    className="h-9"
                  />
                </div>
              </div>

              {/* Faixa: data de pagamento */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Pagamento
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={dataPagamentoDe ?? ''}
                    onChange={(e) =>
                      setDataPagamentoDe(e.target.value || null)
                    }
                    className="h-9"
                  />
                  <Input
                    type="date"
                    value={dataPagamentoAte ?? ''}
                    onChange={(e) =>
                      setDataPagamentoAte(e.target.value || null)
                    }
                    className="h-9"
                  />
                </div>
              </div>

              <Separator />

              {/* Apenas aprovadas sem pagamento */}
              <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="only-unpaid" className="text-sm">
                    Apenas aprovadas sem pagamento
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Comissões aprovadas que ainda não foram vinculadas a um
                    payout.
                  </p>
                </div>
                <Switch
                  id="only-unpaid"
                  checked={onlyUnpaid}
                  onCheckedChange={setOnlyUnpaid}
                />
              </div>
            </div>
          </ScrollArea>

          <div className="border-t p-3 flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAdvancedFilters}
              disabled={advancedFilterCount === 0}
            >
              Limpar
            </Button>
            <Button size="sm" onClick={() => setIsMoreFiltersOpen(false)}>
              Aplicar
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ============================================================= */}
      {/* Diálogo de cancelamento (preservado)                           */}
      {/* ============================================================= */}
      <AlertDialog
        open={!!cancelTarget}
        onOpenChange={(open) => {
          if (!open) {
            setCancelTarget(null);
            setCancelReason('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar comissão</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação altera o status para <b>cancelada</b> e impede que a
              comissão seja paga. Informe um motivo para o histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Motivo (opcional)</Label>
            <Textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Ex.: venda revertida, fraude detectada..."
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancelar comissão
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ================================================================== */}
      {/* v2.2.0 — Modais premium das colunas clicáveis                       */}
      {/*   • DealDetailsModalPremium      → coluna "N. Negócio"             */}
      {/*   • CustomerDetailsModalPremium  → coluna "Cliente"                */}
      {/*   • CommissionPayoutsModal       → coluna "Pagamentos" (drill-down)*/}
      {/* ================================================================== */}
      <DealDetailsModalPremium
        dealId={selectedDealId}
        open={isDealModalOpen}
        onOpenChange={(open) => {
          setIsDealModalOpen(open);
          if (!open) setSelectedDealId(null);
        }}
      />
      <CustomerDetailsModalPremium
        customerId={selectedCustomerId}
        open={isCustomerModalOpen}
        onOpenChange={(open) => {
          setIsCustomerModalOpen(open);
          if (!open) setSelectedCustomerId(null);
        }}
      />
      <CommissionPayoutsModal
        commissionId={payoutsCommissionId}
        open={!!payoutsCommissionId}
        onClose={() => setPayoutsCommissionId(null)}
      />

      {/* v2.3.0 — Sheet lateral de detalhes da comissão.
          Abre via clique na coluna "Ref." OU item "Ver detalhes" do menu.
          Reaproveita os mesmos handlers de aprovar/pagar/cancelar.
          As ações são gated internamente por `canEdit('referral_commissions')`. */}
      <CommissionDetailSheet
        commissionId={detailsCommissionId}
        onClose={() => setDetailsCommissionId(null)}
        onAction={(id, action) => {
          if (action === 'approve') handleApprove(id);
          else if (action === 'mark_paid') handleMarkPaid(id);
          else if (action === 'cancel') setCancelTarget(id);
        }}
        onOpenDeal={(dealId) => {
          setSelectedDealId(dealId);
          setIsDealModalOpen(true);
        }}
        onOpenCustomer={(customerId) => {
          setSelectedCustomerId(customerId);
          setIsCustomerModalOpen(true);
        }}
      />
    </ListPageLayout>
  );
}
